import { useCallback, useEffect, useState } from 'react'

/**
 * Base type constraint for drag payloads.
 * Both DragPayload and DragPayloadSymmetrical satisfy this interface.
 */
type BaseDragPayload = {
  sourceLevelId?: string
  previewGroups?: unknown[]
}

type UseDragPreviewStateOptions<TPayload extends BaseDragPayload> = {
  /** Levels array - used to clear drag state when levels change */
  levels: unknown[]
  /** Callback when drag starts from the ladder (notifies parent) */
  onDragStartFromLadder?: (payload: TPayload) => void
  /** Callback when drag ends from the ladder (notifies parent) */
  onDragEndFromLadder?: () => void
  /** Callback to assign groups to a level on drop */
  assignGroups: (levelId: string, payload: TPayload) => void
}

type UseDragPreviewStateReturn<TPayload extends BaseDragPayload> = {
  /** Handler for when drag starts from a bar segment */
  handleDragStartFromBar: (payload: TPayload) => void
  /** Handler for when drag ends */
  handleDragEndFromBar: () => void
  /** Handler for when drag enters a row */
  handleDragOverRow: (levelId: string) => void
  /** Handler for when drag leaves a row */
  handleDragLeaveRow: () => void
  /** Handler for drop - wraps assignGroups and clears drag state */
  handleDrop: (levelId: string, payload: TPayload) => void
  /** Gets preview groups for a specific level (returns undefined if not applicable) */
  getPreviewGroupsForLevel: (levelId: string) => TPayload['previewGroups']
}

/**
 * Hook to manage drag preview state for the bid ladder.
 * Handles drag start/end, hover tracking, and preview group display.
 *
 * Generic over the payload type to support both asymmetrical (DragPayload)
 * and symmetrical (DragPayloadSymmetrical) markets.
 */
function useDragPreviewState<TPayload extends BaseDragPayload>({
  levels,
  onDragStartFromLadder,
  onDragEndFromLadder,
  assignGroups,
}: UseDragPreviewStateOptions<TPayload>): UseDragPreviewStateReturn<TPayload> {
  const [currentDragPayload, setCurrentDragPayload] = useState<TPayload | null>(null)
  const [hoveredLevelId, setHoveredLevelId] = useState<string | null>(null)

  // Clear drag state when levels change (e.g., when a group is assigned/unassigned)
  // This prevents stale drag state from affecting future drag operations
  useEffect(() => {
    setCurrentDragPayload(null)
    setHoveredLevelId(null)
  }, [levels])

  const handleDragStartFromBar = useCallback(
    (payload: TPayload) => {
      setCurrentDragPayload(payload)
      // Notify parent so sidebar can show drop zone
      onDragStartFromLadder?.(payload)
    },
    [onDragStartFromLadder],
  )

  const handleDragEndFromBar = useCallback(() => {
    setCurrentDragPayload(null)
    setHoveredLevelId(null)
    // Notify parent
    onDragEndFromLadder?.()
  }, [onDragEndFromLadder])

  const handleDragOverRow = useCallback((levelId: string) => {
    setHoveredLevelId(levelId)
  }, [])

  const handleDragLeaveRow = useCallback(() => {
    setHoveredLevelId(null)
  }, [])

  // Get preview groups for a specific level
  const getPreviewGroupsForLevel = useCallback(
    (levelId: string): TPayload['previewGroups'] => {
      if (!currentDragPayload || !hoveredLevelId || hoveredLevelId !== levelId) {
        return undefined
      }
      // Don't show preview on source level
      if (currentDragPayload.sourceLevelId === levelId) {
        return undefined
      }
      return currentDragPayload.previewGroups
    },
    [currentDragPayload, hoveredLevelId],
  )

  // Wrap assignGroups to also clear drag state after drop
  const handleDrop = useCallback(
    (levelId: string, payload: TPayload) => {
      assignGroups(levelId, payload)
      // Clear drag preview state after drop
      setCurrentDragPayload(null)
      setHoveredLevelId(null)
    },
    [assignGroups],
  )

  return {
    handleDragStartFromBar,
    handleDragEndFromBar,
    handleDragOverRow,
    handleDragLeaveRow,
    handleDrop,
    getPreviewGroupsForLevel,
  }
}

export { useDragPreviewState }
export type { UseDragPreviewStateOptions, UseDragPreviewStateReturn }
